import React, { useMemo, useRef, useState, useCallback } from 'react';
import { GoogleMap, Marker, OverlayView, Polygon, Polyline, withGoogleMap, withScriptjs } from 'react-google-maps';
import InfoBox from 'react-google-maps/lib/components/addons/InfoBox';
import { compose, withProps } from 'recompose';
import moment from 'moment';
import pinT1Icon from 'Components/MapMarkers/pinT1.png';
import { GOOGLE_API_KEY } from 'Constants/commonConstants';
import { getPingColor, getRouteColor, getRoutePointColor } from '../utils/legend';
import { bufferPath } from '../utils/geofence';
import { VEHICLE_ICON_PATH, WAY_POINT_ICON_PATH } from '../utils/icons';
import styles from '../TripTrackingV2.module.scss';

// Placeholder until the color rule (based on trip/status data) is decided.
const DEFAULT_VEHICLE_COLOR = '#004792';

const getVehicleIcon = (color) => ({
  path: VEHICLE_ICON_PATH,
  fillColor: color || DEFAULT_VEHICLE_COLOR,
  fillOpacity: 1,
  strokeWeight: 1,
  strokeColor: '#ffffff',
  scale: 1.4,
  anchor: new google.maps.Point(12, 12),
});

// Clicking a route highlights it (thicker, full opacity) and dims the
// others, like Google Maps' own alternate-route picker. Hovering nudges the
// weight up too, as a cue that the line is clickable.
const getPolylineOptions = (color, isSelected, isHovered, hasSelection) => ({
  strokeColor: color,
  strokeOpacity: hasSelection && !isSelected ? 0.25 : 1,
  strokeWeight: isSelected ? 5 : (isHovered ? 4 : 3),
  zIndex: isSelected ? 3 : 2,
  cursor: 'pointer',
});

const isSamePoint = (a, b) => !!a && !!b && a.latitude === b.latitude && a.longitude === b.longitude;

// turf's buffer() is real geometry work (arcs + polygon union), not cheap
// like the old hand-rolled offset math - without memoizing it, it was
// recomputing from scratch on every re-render of the map (every route
// hover/select, every ping hover...), which is what made toggling Geofence
// on feel laggy. Keyed on the raw encoded polyline + radius (stable across
// re-renders) rather than the decoded path (a new array every render).
const RouteGeofence = ({ polyline, radiusMeters, color }) => {
  const paths = useMemo(
    () => bufferPath(google.maps.geometry.encoding.decodePath(polyline), radiusMeters),
    [polyline, radiusMeters]
  );
  return <Polygon paths={paths} options={{ fillColor: color, fillOpacity: 0.15, strokeOpacity: 0, zIndex: 1 }} />;
};

// A hover tooltip that hides the instant the mouse leaves a small marker
// never gives you time to reach the tooltip itself (e.g. to click its copy
// button) - it closes before the cursor gets there. This delays the hide
// briefly instead of hiding immediately, and `cancelHide`/`bindHoverTarget`
// let the tooltip's own content cancel that pending hide when the mouse
// moves onto it, and re-arm it on leaving, so it only truly closes once the
// mouse has left both the marker and the tooltip.
const HOVER_HIDE_DELAY_MS = 300;

const useHoverTooltip = () => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef(null);

  const cancelHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    cancelHide();
    setIsVisible(true);
  }, [cancelHide]);

  const scheduleHide = useCallback(() => {
    cancelHide();
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), HOVER_HIDE_DELAY_MS);
  }, [cancelHide]);

  const hideNow = useCallback(() => {
    cancelHide();
    setIsVisible(false);
  }, [cancelHide]);

  // Spread onto the tooltip content's wrapping element so hovering the
  // tooltip itself keeps it open, and leaving it resumes the close timer.
  const bindTooltip = { onMouseEnter: cancelHide, onMouseLeave: scheduleHide };

  return { isVisible, show, scheduleHide, hideNow, bindTooltip };
};

const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Copies "lat, lng" to the clipboard. stopPropagation matters here - the
// InfoBox this sits in has enableEventPropagation:true, so an unstopped
// click would fall through to the map's own onClick and deselect whatever
// route was selected.
const CopyCoordinates = ({ lat, lng }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (event) => {
    event.stopPropagation();
    navigator.clipboard.writeText(`${lat}, ${lng}`)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((error) => console.log('[TripTrackingV2] copy coordinates error:', error));
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy coordinates"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: '22px',
        height: '22px',
        marginLeft: '10px',
        padding: 0,
        border: '1px solid #e7e9ec',
        borderRadius: '4px',
        background: copied ? '#eafaf1' : '#ffffff',
        color: copied ? '#1e9e57' : '#6b7280',
        cursor: 'pointer',
      }}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
};

// Shared tooltip content for ping/waypoint hover cards and the click-
// anywhere coordinate card - a title/subtitle, the raw lat/lng (so it's
// copyable even when there's no human-readable address), and a copy button.
// `onClose` is only used by the click-triggered (not hover-triggered) card,
// since hover cards already dismiss themselves on mouse-out.
const MapTooltipCard = ({ color, title, subtitle, lat, lng, onClose }) => (
  <div>
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        background: '#ffffff',
        padding: '10px 10px 10px 12px',
        borderRadius: '8px',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
        fontSize: '13px',
        lineHeight: 1.45,
        maxWidth: '260px',
        whiteSpace: 'normal',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#1f2733', overflowWrap: 'anywhere' }}>{title}</div>
        {subtitle && <div style={{ color: '#8a94a6', marginTop: '3px', fontSize: '11px', overflowWrap: 'anywhere' }}>{subtitle}</div>}
        <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '11px', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
          {lat.toFixed(6)}, {lng.toFixed(6)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <CopyCoordinates lat={lat} lng={lng} />
        {onClose && (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); onClose(); }}
            title="Close"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              width: '22px', height: '22px', marginLeft: '6px', padding: 0, border: 'none',
              background: 'transparent', color: '#8a94a6', cursor: 'pointer', fontSize: '15px', lineHeight: 1,
            }}
          >
            &times;
          </button>
        )}
      </div>
    </div>
    <div
      style={{
        width: 0,
        height: 0,
        margin: '0 0 0 16px',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #ffffff',
        filter: 'drop-shadow(0 2px 1px rgba(0,0,0,0.12))',
      }}
    />
  </div>
);

const WAY_POINT_PIN_SIZE = 30;

// Same outline as WAY_POINT_ICON_PATH, but with its hollow center ring kept
// (that's the bit that got dropped for the S/D pins, since it left no solid
// background for their label text) - this is the classic Google-pin look:
// rounded head, pointed tip, white ring punched through the middle. Used
// only where there's no label sitting inside the pin, i.e. the click-
// anywhere marker.
const DROPPED_PIN_ICON_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';

// Anchors the overlay at the pin's tip (bottom-center) instead of the
// default top-left, matching how map pins are conventionally placed.
const anchorPinTip = (width, height) => ({ x: -(width / 2), y: -height });

// Rendered as a plain HTML/SVG overlay (via OverlayView) rather than a
// Marker + Symbol icon + label - Marker's native label positioning doesn't
// cooperate with a custom `anchor` on a path-based icon, so the label text
// silently fails to render. An SVG <text> node sidesteps that entirely. The
// overlay is plain DOM, so hover can just use normal React mouse handlers.
const WayPointPin = ({ point, color, label, title, subtitle }) => {
  const { isVisible, show, scheduleHide, hideNow, bindTooltip } = useHoverTooltip();

  return (
    <>
      <OverlayView position={point} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={anchorPinTip}>
        <svg
          width={WAY_POINT_PIN_SIZE}
          height={WAY_POINT_PIN_SIZE}
          viewBox="0 0 24 24"
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
        >
          <path d={WAY_POINT_ICON_PATH} fill={color} stroke="#ffffff" strokeWidth="1" />
          <text x="12" y="9" textAnchor="middle" dominantBaseline="central" fontSize="7" fontWeight="700" fill="#ffffff">
            {label}
          </text>
        </svg>
      </OverlayView>
      {isVisible && (
        <InfoBox
          position={new google.maps.LatLng(point.lat, point.lng)}
          options={{ closeBoxURL: '', enableEventPropagation: true, alignBottom: true, pixelOffset: new google.maps.Size(-16, -14) }}
        >
          <div {...bindTooltip}>
            <MapTooltipCard color={color} title={title} subtitle={subtitle} lat={point.lat} lng={point.lng} onClose={hideNow} />
          </div>
        </InfoBox>
      )}
    </>
  );
};

// A black outline reads fine against a light ping color, but merges into
// near-invisibility against a dark one (e.g. the #581845 "DELAYED" plum) -
// so the outline switches to white for dark fills based on perceived
// brightness (standard YIQ luminance formula).
const getContrastStroke = (hexColor) => {
  const hex = (hexColor || '').replace('#', '');
  if (hex.length !== 6) return '#000000';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 140 ? '#000000' : '#ffffff';
};

// GPS pings along the route, colored via ping_legend. Hovering shows the
// place/time in a lightweight tooltip, same as the old tracking screen.
const PingMarker = ({ location, color }) => {
  const { isVisible, show, scheduleHide, hideNow, bindTooltip } = useHoverTooltip();
  // The track API sends locations[].latitude/longitude as strings (unlike
  // routes' way_points, which are already numbers) - Marker silently drops
  // a position with string coordinates, so it must be coerced here.
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);

  return (
    <>
      <Marker
        position={{ lat, lng }}
        icon={{
          path: google.maps.SymbolPath.CIRCLE,
          scale: isVisible ? 6.5 : 5,
          fillColor: color,
          fillOpacity: 0.95,
          strokeWeight: isVisible ? 2 : 1.5,
          strokeColor: getContrastStroke(color),
        }}
        onMouseOver={show}
        onMouseOut={scheduleHide}
        zIndex={isVisible ? 16 : 15}
      />
      {isVisible && (
        <InfoBox
          position={new google.maps.LatLng(lat, lng)}
          options={{ closeBoxURL: '', enableEventPropagation: true, alignBottom: true, pixelOffset: new google.maps.Size(-16, -14) }}
        >
          <div {...bindTooltip}>
            <MapTooltipCard
              color={color}
              title={location.display_name || location.location || 'Location'}
              subtitle={location.date ? moment(location.date).format('DD MMM YYYY, HH:mm') : null}
              lat={lat}
              lng={lng}
              onClose={hideNow}
            />
          </div>
        </InfoBox>
      )}
    </>
  );
};

const FASTTAG_ACCENT_COLOR = '#F5A623';

// Toll plaza / FastTag markers. The old screen only ever read
// toll_plaza_geocode from these records (see useFastTag) - other fields
// (a name, amount, time) aren't confirmed yet, so the tooltip falls back to
// just coordinates + a generic title until the real field names are known.
const FastTagMarker = ({ point }) => {
  const { isVisible, show, scheduleHide, hideNow, bindTooltip } = useHoverTooltip();
  const record = point.record || {};
  const title = record.toll_plaza_name || record.plaza_name || record.name || record.location || 'Toll Plaza';
  const subtitle = record.amount ? `Amount: ${record.amount}` : (record.time || record.date || null);

  return (
    <>
      <Marker
        position={{ lat: point.latitude, lng: point.longitude }}
        icon={{ url: pinT1Icon, scaledSize: new google.maps.Size(22, 33) }}
        onMouseOver={show}
        onMouseOut={scheduleHide}
        zIndex={isVisible ? 2 : 1}
      />
      {isVisible && (
        <InfoBox
          position={new google.maps.LatLng(point.latitude, point.longitude)}
          // Google's default anchor for an image icon is its bottom-center,
          // so the 33px-tall pin sits entirely above the geo point - needs a
          // bigger push than the SVG pins to actually clear it.
          options={{ closeBoxURL: '', enableEventPropagation: true, alignBottom: true, pixelOffset: new google.maps.Size(-11, -40) }}
        >
          <div {...bindTooltip}>
            <MapTooltipCard
              color={FASTTAG_ACCENT_COLOR}
              title={title}
              subtitle={subtitle}
              lat={point.latitude}
              lng={point.longitude}
              onClose={hideNow}
            />
          </div>
        </InfoBox>
      )}
    </>
  );
};

const TripMapBase = ({
  routes = [], routeLegend = [], pingLegend = [], routePointLegend = [], locations = [], fastTagPoints = [],
  lastKnownLocation, vehicleColor, showGeofence, isMeasuring, measurePoints = [], onAddMeasurePoint,
  source, destination,
}) => {
  // Source/destination pins now come straight from the track API's own
  // source/destination fields - same reference the old tracking screen
  // uses - rather than being inferred from the routes API's way_points.
  const sourcePoint = source && source.latitude ? { lat: source.latitude, lng: source.longitude } : null;
  const destinationPoint = destination && destination.latitude ? { lat: destination.latitude, lng: destination.longitude } : null;
  const center = sourcePoint || { lat: 20.5937, lng: 78.9629 };
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null);
  const [hoveredRouteIndex, setHoveredRouteIndex] = useState(null);
  // Coordinate card for a plain click on empty map space (not on a marker or
  // route, which have their own click handlers and don't bubble up here).
  const [clickedPoint, setClickedPoint] = useState(null);
  // `ref` on GoogleMap fires on every re-render, not just the first mount -
  // without this guard, hovering a ping (which changes state) re-triggers
  // fitBounds and resets whatever pan/zoom the user was at.
  const hasFitBounds = useRef(false);

  const onMapMounted = (map) => {
    if (!map || hasFitBounds.current || (!sourcePoint && !destinationPoint)) return;
    const bounds = new google.maps.LatLngBounds();
    if (sourcePoint) bounds.extend(sourcePoint);
    if (destinationPoint) bounds.extend(destinationPoint);
    map.fitBounds(bounds);
    hasFitBounds.current = true;
  };

  const toggleRouteSelection = (index) => {
    setSelectedRouteIndex((prev) => (prev === index ? null : index));
  };

  const handleMapClick = (event) => {
    setSelectedRouteIndex(null);
    if (isMeasuring) {
      onAddMeasurePoint({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    } else {
      setClickedPoint({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    }
  };

  return (
    <GoogleMap
      defaultZoom={7}
      defaultCenter={center}
      ref={onMapMounted}
      onClick={handleMapClick}
      // Google's own fullscreen control fullscreens just the map's inner div,
      // which drops everything rendered as a sibling of it (the layers
      // drawer included) - a custom fullscreen toggle on the whole wrapper
      // is used instead, see MapLayersDrawer's fullscreen button.
      options={{ fullscreenControl: false }}
    >
      {routes.map((route, index) => {
        const path = google.maps.geometry.encoding.decodePath(route.polyline);
        const color = getRouteColor(route.data_source, routeLegend);
        const key = `${route.route_id}_${route.data_source}_${index}`;
        const isSelected = selectedRouteIndex === index;
        return (
          <React.Fragment key={key}>
            {showGeofence && route.geofence_radius > 0 && (
              <RouteGeofence polyline={route.polyline} radiusMeters={route.geofence_radius} color={color} />
            )}
            <Polyline
              path={path}
              options={getPolylineOptions(color, isSelected, hoveredRouteIndex === index, selectedRouteIndex !== null)}
              onClick={() => toggleRouteSelection(index)}
              onMouseOver={() => setHoveredRouteIndex(index)}
              onMouseOut={() => setHoveredRouteIndex((prev) => (prev === index ? null : prev))}
            />
          </React.Fragment>
        );
      })}
      {sourcePoint && (
        <WayPointPin
          point={sourcePoint}
          color={getRoutePointColor(0, routePointLegend)}
          label="S"
          title={source.display_name || source.location || 'Source'}
          subtitle="Source"
        />
      )}
      {destinationPoint && (
        <WayPointPin
          point={destinationPoint}
          color={getRoutePointColor(1, routePointLegend)}
          label="D"
          title={destination.display_name || destination.location || 'Destination'}
          subtitle="Destination"
        />
      )}
      {locations.filter((location) => !isSamePoint(location, lastKnownLocation)).map((location, index) => {
        const pingColor = getPingColor(location.type, location.sub_type, pingLegend);
        console.log('[TripTrackingV2] ping color:', { type: location.type, sub_type: location.sub_type, pingColor, pingLegend });
        return <PingMarker key={`ping_${index}`} location={location} color={pingColor} />;
      })}
      {fastTagPoints.map((point, index) => (
        <FastTagMarker key={`fastag_${index}`} point={point} />
      ))}
      {lastKnownLocation && lastKnownLocation.latitude && (
        <Marker
          position={{ lat: lastKnownLocation.latitude, lng: lastKnownLocation.longitude }}
          icon={getVehicleIcon(vehicleColor)}
          zIndex={20}
        />
      )}
      {measurePoints.length > 0 && (
        <>
          {measurePoints.length > 1 && (
            <Polyline
              path={measurePoints}
              options={{ strokeColor: '#ff5722', strokeOpacity: 1, strokeWeight: 3, zIndex: 25 }}
            />
          )}
          {measurePoints.map((point, index) => (
            <Marker
              key={`measure_${index}`}
              position={point}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 5,
                fillColor: '#ff5722',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 1.5,
              }}
              zIndex={26}
            />
          ))}
        </>
      )}
      {clickedPoint && !isMeasuring && (
        <React.Fragment>
          {/* Marker Symbol icons are Canvas-rendered and don't reliably
              honor a path's hole subpath (unlike real SVG, which the S/D
              pins already rely on) - same OverlayView + raw <svg> approach
              here so the ring actually shows, and sizing is exact pixels
              rather than fiddly Symbol `scale` units. */}
          <OverlayView position={clickedPoint} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={anchorPinTip}>
            <svg width={16} height={16} viewBox="0 0 24 24">
              <path d={DROPPED_PIN_ICON_PATH} fill="#4285F4" stroke="#1a56c4" strokeWidth="1" />
            </svg>
          </OverlayView>
          <InfoBox
            position={new google.maps.LatLng(clickedPoint.lat, clickedPoint.lng)}
            // This pin is only 16px tall (vs. 30px for the ping/waypoint
            // pins that share this -16,-14 offset), so it needs a bigger
            // vertical push to actually clear the top of the pin instead of
            // landing inside it.
            options={{ closeBoxURL: '', enableEventPropagation: true, alignBottom: true, pixelOffset: new google.maps.Size(-16, -26) }}
          >
            <MapTooltipCard
              color="#4285F4"
              title="Clicked location"
              lat={clickedPoint.lat}
              lng={clickedPoint.lng}
              onClose={() => setClickedPoint(null)}
            />
          </InfoBox>
        </React.Fragment>
      )}
    </GoogleMap>
  );
};

const TripMap = compose(
  withProps({
    googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&v=3.exp&libraries=geometry,drawing,places`,
    loadingElement: <div style={{ height: '100%' }} />,
    containerElement: <div className={styles.mapPane} />,
    mapElement: <div style={{ height: '100%' }} />,
  }),
  withScriptjs,
  withGoogleMap,
)(TripMapBase);

export default TripMap;
